import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { eventsService } from '../../services/api';

const EventParticipants = ({ eventId }) => {
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (eventId) {
      loadParticipants();
    }
  }, [eventId]);

  const loadParticipants = async () => {
    try {
      setLoading(true);
      const response = await eventsService.getEvent(eventId);
      // Handle nested response structure
      const eventData = response.data?.data || response.data;
      setParticipants(eventData.participants || []);
    } catch (err) {
      setError('Failed to load participants');
      console.error('Error loading participants:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (participant) => {
    const user = participant.user || participant;
    const name = user.given_name && user.family_name 
      ? `${user.given_name} ${user.family_name}`
      : user.display_name || user.email || 'Anonymous';
    
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getDisplayName = (participant) => {
    const user = participant.user || participant;
    if (user.given_name && user.family_name) {
      return `${user.given_name} ${user.family_name}`;
    }
    return user.display_name || 'Anonymous User';
  };

  const getEmail = (participant) => {
    const user = participant.user || participant;
    return user.email || 'No email';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (participants.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No participants yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Participants will appear here once they join the event
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <PersonIcon color="primary" />
            </Grid>
            <Grid item xs>
              <Typography variant="h6">
                {participants.length} Participant{participants.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users who have joined this event
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Participants List */}
      <List>
        {participants.map((participant, index) => (
          <React.Fragment key={participant.user_id}>
            <ListItem alignItems="flex-start">
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {getInitials(participant)}
                </Avatar>
              </ListItemAvatar>
              
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">
                      {getDisplayName(participant)}
                    </Typography>
                    <Chip
                      label={participant.submitted_at ? "Submitted" : "Participant"}
                      size="small"
                      color={participant.submitted_at ? "success" : "primary"}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 1 }}>
                    {/* Email */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <EmailIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {getEmail(participant)}
                      </Typography>
                    </Box>
                    
                    {/* Join Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        Joined {formatDate(participant.joined_at)}
                      </Typography>
                    </Box>

                    {/* Submission Date (if available) */}
                    {participant.submitted_at && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <CalendarIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          Submitted {formatDate(participant.submitted_at)}
                        </Typography>
                      </Box>
                    )}

                    {/* Bio (if available) */}
                    {participant.user?.bio && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                        "{participant.user.bio}"
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
            
            {index < participants.length - 1 && <Divider variant="inset" component="li" />}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default EventParticipants;
